import { LocalShipping } from "@mui/icons-material";
import CancelRounded from "@mui/icons-material/CancelRounded";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import GroupsIcon from "@mui/icons-material/Groups";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import React, { useRef, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { ampli } from "@/config";
import type { useDriverFilters } from "@/hooks/driver/useDriverFilters";
import type { DriverRow, DriverViewTab, TagDefinition } from "@/types/driver";
import type { DataTableInstance } from "@/ui/dataTableFeatures";
import { zinc } from "@/ui/tokens";
import { redColor } from "@/utils/color";
import ColumnManager from "./ColumnManager";
import type { TagFilterState } from "./DriverColumnFilters";

export const DriverTabs = React.memo(function DriverTabs({
  filters,
  myDriverUnreadCount,
}: {
  filters: ReturnType<typeof useDriverFilters>;
  myDriverUnreadCount: number;
}) {
  const theme = useTheme();
  const tabs: { key: DriverViewTab; label: string }[] = [
    { key: "my", label: "My Drivers" },
    { key: "all", label: "All Drivers" },
  ];

  const tabTypography = {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", px: 1 }}>
      {tabs.map((tab) => {
        const isActive = tab.key === filters.effectiveTab;
        return (
          <ButtonBase
            key={tab.key}
            data-sb={TestIds.driverView.tab}
            onClick={() => filters.setTab(tab.key)}
            sx={{
              borderTopLeftRadius: 6,
              borderTopRightRadius: 6,
              cursor: "pointer",
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              ...tabTypography,
              ...(isActive
                ? {
                    bgcolor: zinc[800],
                    color: "white",
                    fontWeight: 600,
                    borderBottom: "2px solid white",
                  }
                : {
                    bgcolor: "transparent",
                    color: zinc[500],
                    fontWeight: 400,
                    borderBottom: "2px solid transparent",
                    "&:hover": { color: zinc[300] },
                  }),
            }}
          >
            {tab.key === "my" && <NotificationsIcon sx={{ fontSize: 18 }} />}
            <span>{tab.label}</span>
            {tab.key === "my" && myDriverUnreadCount > 0 && (
              <Box
                component="span"
                data-sb={TestIds.driverView.tabUnreadBadge}
                sx={{
                  minWidth: 18,
                  height: 18,
                  px: 0.5,
                  borderRadius: "9999px",
                  bgcolor: redColor,
                  color: "white",
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  lineHeight: "18px",
                  textAlign: "center",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              >
                {myDriverUnreadCount}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
});

export const TeamDropdown = React.memo(function TeamDropdown({
  filters,
  onTeamChange,
}: {
  filters: ReturnType<typeof useDriverFilters>;
  onTeamChange: (team: string) => void;
}) {
  const teamMenuAnchor = useRef<HTMLButtonElement>(null);
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);

  if (filters.availableTeams.length < 2) return null;

  return (
    <>
      <ButtonBase
        ref={teamMenuAnchor}
        data-sb={TestIds.driverView.teamDropdown}
        onClick={() => filters.availableTeams.length > 1 && setTeamMenuOpen(true)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.5,
          color: "white",
          whiteSpace: "nowrap",
          fontSize: "1rem",
          cursor: filters.availableTeams.length > 1 ? "pointer" : "default",
        }}
      >
        <GroupsIcon sx={{ fontSize: 18 }} />
        <span>{filters.team}</span>
        {filters.availableTeams.length > 1 && <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
      </ButtonBase>
      {filters.availableTeams.length > 1 && (
        <Menu
          anchorEl={teamMenuAnchor.current}
          open={teamMenuOpen}
          onClose={() => setTeamMenuOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                bgcolor: zinc[800],
                border: `1px solid ${zinc[600]}`,
                boxShadow: "0 0 0 4px rgba(0,0,0,0.5)",
                minWidth: 180,
              },
            },
          }}
        >
          {filters.availableTeams.map((t) => (
            <MenuItem
              key={t}
              data-sb={TestIds.driverView.teamOption}
              selected={t === filters.team}
              onClick={() => {
                onTeamChange(t);
                setTeamMenuOpen(false);
                ampli.changeTeam({ teamName: t });
              }}
              sx={{
                color: "white",
                "&.Mui-selected": { bgcolor: zinc[700] },
                "&:hover": { bgcolor: zinc[700] },
              }}
            >
              {t}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
});

// Not memoized: the column manager reads live table state (pinning/order/visibility) off the stable `table` ref, so a
// memo on the stable ref would leave the manager stale after edits.
export default function DriverToolbar({
  filters,
  selectedCount,
  onFleetMessage,
  table,
  onColumnPinningChange,
  onResetColumns,
  tags,
  tagFilterState,
}: {
  filters: ReturnType<typeof useDriverFilters>;
  selectedCount: number;
  onFleetMessage: () => void;
  table: DataTableInstance<DriverRow>;
  onColumnPinningChange: (left: string[]) => void;
  onResetColumns: () => void | Promise<void>;
  tags: TagDefinition[];
  tagFilterState: TagFilterState;
}) {
  const theme = useTheme();
  const [searchFocused, setSearchFocused] = useState(false);

  const glowingSx = selectedCount > 0 ? { boxShadow: `0 0 0 1px ${theme.palette.brandPrimary[300]}` } : {};

  return (
    <Box sx={{ display: "flex", gap: 1, bgcolor: zinc[800], p: 1 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ position: "relative" }}>
          <Box
            component="label"
            htmlFor="filterDrivers"
            sx={{
              width: 35,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 10,
              color: searchFocused ? "white" : zinc[400],
            }}
          >
            <SearchIcon />
          </Box>
          <InputBase
            data-sb={TestIds.driverView.search}
            onBlur={() => setSearchFocused(false)}
            onFocus={() => setSearchFocused(true)}
            id="filterDrivers"
            placeholder="Search Drivers"
            value={filters.search}
            onChange={(e) => filters.setSearch(e.target.value)}
            sx={{
              bgcolor: zinc[600],
              width: "100%",
              height: "100%",
              py: 1,
              pl: 5,
              pr: 5,
              borderRadius: 1,
              color: "white",
              "& .MuiInputBase-input": {
                p: 0,
                color: "white",
                "&::placeholder": { color: zinc[400], opacity: 1 },
              },
              "&.Mui-focused": {
                bgcolor: "rgba(82,82,91,0.9)",
                outline: `2px solid ${theme.palette.brandPrimary[500]}`,
              },
            }}
          />
          {filters.search.length > 0 && (
            <ButtonBase
              data-sb={TestIds.driverView.searchClear}
              onClick={() => filters.setSearch("")}
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                height: "100%",
                px: 1,
                borderRadius: 1,
                color: "white",
              }}
            >
              <CancelRounded />
            </ButtonBase>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <ColumnManager
          table={table}
          onColumnPinningChange={onColumnPinningChange}
          onResetColumns={onResetColumns}
          tags={tags}
        />
        {(filters.hasActiveFilters || tagFilterState.hasActiveTagFilters) && (
          <ButtonBase
            data-sb={TestIds.driverView.clearFilters}
            onClick={() => {
              filters.clearAll();
              tagFilterState.clearAllTagFilters();
              ampli.clearAllFilters();
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: zinc[600],
              color: zinc[300],
              fontSize: "1rem",
              "&:hover": { bgcolor: zinc[500], color: "white" },
              cursor: "pointer",
            }}
          >
            <FilterListOffIcon sx={{ fontSize: 18 }} />
            <span>Clear Filters</span>
          </ButtonBase>
        )}
        <ButtonBase
          data-sb={TestIds.fleet.open}
          onClick={onFleetMessage}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            fontSize: "1rem",
            bgcolor: theme.palette.brandPrimary[500],
            color: selectedCount > 0 ? "white" : "inherit",
            "&:hover": { bgcolor: theme.palette.brandPrimary[600] },
            cursor: "pointer",
            ...glowingSx,
          }}
        >
          <LocalShipping sx={{ color: selectedCount > 0 ? "white" : "inherit" }} />
          <span>Fleet Message</span>
        </ButtonBase>
      </Box>
    </Box>
  );
}
